import styled, { css } from 'styled-components'
import { GridProps, GridItemProps } from './Grid.types'

/**
 * Breakpoints aligned with theme.breakpoints for consistency:
 * - xs: 0 (base/mobile)
 * - sm: 640px (theme.breakpoints.mobile)
 * - md: 1024px (theme.breakpoints.tablet)
 * - lg: 1280px (theme.breakpoints.desktop)
 * - xl: 1536px (large desktop)
 */
const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 1024,
  lg: 1280,
  xl: 1536,
}

const getSpacingValue = (
  value: string | number | undefined
): string | undefined => {
  if (value === undefined) return undefined
  if (typeof value === 'number') return `${value}px`
  return value
}

const getGridColumns = (span?: number): string | undefined => {
  if (!span) return undefined
  return `span ${span} / span ${span}`
}

const containerStyles = css<GridProps>`
  display: grid;

  ${({ columns }) =>
    columns &&
    `grid-template-columns: ${
      typeof columns === 'number' ? `repeat(${columns}, 1fr)` : columns
    };`}

  ${({ rows }) =>
    rows &&
    `grid-template-rows: ${
      typeof rows === 'number' ? `repeat(${rows}, 1fr)` : rows
    };`}
  
  ${({ templateColumns }) =>
    templateColumns && `grid-template-columns: ${templateColumns};`}
  ${({ templateRows }) =>
    templateRows && `grid-template-rows: ${templateRows};`}
  ${({ templateAreas }) =>
    templateAreas && `grid-template-areas: ${templateAreas};`}
  
  ${({ gap, spacing }) => {
    const gapValue = gap ?? spacing
    return gapValue !== undefined && `gap: ${getSpacingValue(gapValue)};`
  }}
  ${({ rowGap }) =>
    rowGap !== undefined && `row-gap: ${getSpacingValue(rowGap)};`}
  ${({ columnGap }) =>
    columnGap !== undefined && `column-gap: ${getSpacingValue(columnGap)};`}
  
  ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
  ${({ justifyItems }) => justifyItems && `justify-items: ${justifyItems};`}
  ${({ alignContent }) => alignContent && `align-content: ${alignContent};`}
  ${({ justifyContent }) =>
    justifyContent && `justify-content: ${justifyContent};`}
  
  ${({ autoFlow }) => autoFlow && `grid-auto-flow: ${autoFlow};`}
  ${({ autoColumns }) => autoColumns && `grid-auto-columns: ${autoColumns};`}
  ${({ autoRows }) => autoRows && `grid-auto-rows: ${autoRows};`}
`

const flexContainerStyles = css<GridProps>`
  display: flex;
  flex-wrap: ${({ wrap = 'wrap' }) => wrap};
  flex-direction: ${({ direction = 'row' }) => direction};

  ${({ gap, spacing }) => {
    const gapValue = gap ?? (spacing ? spacing * 8 : undefined)
    return gapValue !== undefined && `gap: ${getSpacingValue(gapValue)};`
  }}

  ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
  ${({ justifyContent }) =>
    justifyContent && `justify-content: ${justifyContent};`}
`

export const StyledGrid = styled.div<GridProps>`
  ${({
    container = true,
    columns,
    rows,
    templateColumns,
    templateRows,
    templateAreas,
  }) =>
    container &&
    (columns || rows || templateColumns || templateRows || templateAreas)
      ? containerStyles
      : flexContainerStyles}

  /* Tablet: Collapse multi-column grids on tablet (unless tabletColumns is specified) */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md -
  1}px) {
    ${({ columns, tabletColumns }) => {
      // If tabletColumns is specified, use it
      if (tabletColumns !== undefined) {
        const tabletGridColumns =
          typeof tabletColumns === 'number'
            ? `repeat(${tabletColumns}, 1fr)`
            : tabletColumns
        return css`
          grid-template-columns: ${tabletGridColumns};
        `
      }
      // Default behavior: collapse to 2 columns if columns > 2
      if (columns && typeof columns === 'number' && columns > 2) {
        return css`
          grid-template-columns: repeat(2, 1fr);
        `
      }
      return ''
    }}
  }

  /* Mobile-first: Collapse multi-column grids on mobile (unless mobileColumns is specified) */
  @media (max-width: ${BREAKPOINTS.sm - 1}px) {
    ${({ columns, mobileColumns }) => {
      // If mobileColumns is specified, use it
      if (mobileColumns !== undefined) {
        const mobileGridColumns =
          typeof mobileColumns === 'number'
            ? `repeat(${mobileColumns}, 1fr)`
            : mobileColumns
        return css`
          grid-template-columns: ${mobileGridColumns};
        `
      }
      // Default behavior: collapse to 1 column if columns > 1
      if (columns && typeof columns === 'number' && columns > 1) {
        return css`
          grid-template-columns: 1fr;
        `
      }
      return ''
    }}
  }
`

const getResponsiveWidth = (size?: number): string => {
  if (!size) return 'auto'
  return `${(size / 12) * 100}%`
}

export const StyledGridItem = styled.div<GridItemProps>`
  /* Mobile-first: Default to full width on mobile if no xs specified */
  flex: 0 0 100%;
  max-width: 100%;

  ${({ gridColumn, colSpan }) => {
    const column = gridColumn ?? (colSpan ? getGridColumns(colSpan) : undefined)
    return column && `grid-column: ${column};`
  }}

  ${({ gridRow, rowSpan }) => {
    const row =
      gridRow ?? (rowSpan ? `span ${rowSpan} / span ${rowSpan}` : undefined)
    return row && `grid-row: ${row};`
  }}

  ${({ gridArea }) => gridArea && `grid-area: ${gridArea};`}

  ${({ colStart }) => colStart && `grid-column-start: ${colStart};`}
  ${({ colEnd }) => colEnd && `grid-column-end: ${colEnd};`}
  ${({ rowStart }) => rowStart && `grid-row-start: ${rowStart};`}
  ${({ rowEnd }) => rowEnd && `grid-row-end: ${rowEnd};`}

  ${({ alignSelf }) => alignSelf && `align-self: ${alignSelf};`}
  ${({ justifySelf }) => justifySelf && `justify-self: ${justifySelf};`}

  /* Responsive widths for flex-based grid (mobile-first approach) */
  /* xs: Base mobile styles (0px and up) */
  ${({ xs }) =>
    xs &&
    `
    flex: 0 0 ${getResponsiveWidth(xs)};
    max-width: ${getResponsiveWidth(xs)};
  `}

  /* sm: Small screens (640px and up - theme.breakpoints.mobile) */
  ${({ sm }) =>
    sm &&
    `
    @media (min-width: ${BREAKPOINTS.sm}px) {
      flex: 0 0 ${getResponsiveWidth(sm)};
      max-width: ${getResponsiveWidth(sm)};
    }
  `}

  /* md: Medium screens (1024px and up - theme.breakpoints.tablet) */
  ${({ md }) =>
    md &&
    `
    @media (min-width: ${BREAKPOINTS.md}px) {
      flex: 0 0 ${getResponsiveWidth(md)};
      max-width: ${getResponsiveWidth(md)};
    }
  `}

  /* lg: Large screens (1280px and up - theme.breakpoints.desktop) */
  ${({ lg }) =>
    lg &&
    `
    @media (min-width: ${BREAKPOINTS.lg}px) {
      flex: 0 0 ${getResponsiveWidth(lg)};
      max-width: ${getResponsiveWidth(lg)};
    }
  `}

  /* xl: Extra large screens (1536px and up) */
  ${({ xl }) =>
    xl &&
    `
    @media (min-width: ${BREAKPOINTS.xl}px) {
      flex: 0 0 ${getResponsiveWidth(xl)};
      max-width: ${getResponsiveWidth(xl)};
    }
  `}
`
